import { html, property } from 'lit-element';
import { nothing } from 'lit-html';
import { classMap } from 'lit-html/directives/class-map';
import {
  event,
  EventEmitter,
  KatLitElement,
  register,
} from '../../shared/base';
import { checkSlots } from '../../shared/slot-utils';
import baseStyles from '../../shared/base/base.lit.scss';
import styles from './filter-bar.lit.scss';
import getString from './strings';

export enum KatFilterBarAction {
  APPLY = 'apply',
  CLEAR = 'clear',
  FILTERS = 'filters',
  SEARCH = 'search',
  SETTINGS = 'settings',
  SORT = 'sort',
  CREATE = 'create',
  EDIT = 'edit',
}

/**
 * @component {kat-filter-bar} KatFilterBar The filter bar provides a standard UI for controlling the content of lists of items, including tables and grids.
 * @status Production
 * @theme flo
 * @slot default Default filters.
 * @slot quick-filters Quick filters slot should only contain selectable tags.
 * @example All {"show-action-search": true,"show-action-apply": true, "show-action-clear": true, "show-action-filters": true, "show-action-settings": true, "show-action-sort": true}
 * @example UsingSlottedFilters { "show-filter-create": true, "show-filter-edit": true, "show-action-filters": true, "show-action-settings": true, "content": "<section slot=\"quick-filters\"><kat-tag label=\"Preset #1\" type=\"selectable\" selected></kat-tag><kat-tag label=\"Preset #2\" type=\"selectable\"></kat-tag><kat-tag label=\"Preset #3\" type=\"selectable\"></kat-tag></section><kat-input type=\"text\" slot=\"search\" placeholder=\"Search\"></kat-input><kat-dropdown value=\"A\" show-clear><kat-option value=\"A\">Filter 1 A</kat-option><kat-option value=\"B\">Filter 1 B</kat-option></kat-dropdown><kat-dropdown value=\"B\" show-clear><kat-option value=\"A\">Filter 2 A</kat-option><kat-option value=\"B\">Filter 2 B</kat-option></kat-dropdown>" }
 * @a11y {keyboard}
 * @a11y {sr}
 * @a11y {contrast}
 */
@register('kat-filter-bar')
export class KatFilterBar extends KatLitElement {
  static get styles() {
    return [baseStyles, styles];
  }

  /**
   * @classprop {enum} KatFilterBarAction @hidden
   * @enum {value} apply Apply action.
   * @enum {value} clear Clear action.
   * @enum {value} filters Filters action.
   * @enum {value} search Search action.
   * @enum {value} settings Settings action.
   * @enum {value} create Create filter action.
   * @enum {value} edit Edit filter action.
   * @private
   */

  /** Enables the search button */
  @property({ attribute: 'show-action-search' })
  showActionSearch = false;

  /** Enables the Apply action */
  @property({ attribute: 'show-action-apply' })
  showActionApply = false;

  /** Enables the Clear action */
  @property({ attribute: 'show-action-clear' })
  showActionClear = false;

  /** Enables the Filters action */
  @property({ attribute: 'show-action-filters' })
  showActionFilters = false;

  /** Enables the Settings action */
  @property({ attribute: 'show-action-settings' })
  showActionSettings = false;

  /** Enables the Sort action */
  @property({ attribute: 'show-action-sort' })
  showActionSort = false;

  /** Enables the Create filter action */
  @property({ attribute: 'show-filter-create' })
  showFilterCreate = false;

  /** Enables the Edit filter action */
  @property({ attribute: 'show-filter-edit' })
  showFilterEdit = false;

  /**
   * Locale to identify the language used in fixed labels.
   * Use this property/attribute to override the automatic language detection.
   * @type {string}
   */
  @property({ reflect: true })
  locale?: string;

  firstUpdated(): void {
    console.log('first updated');
  }

  /**
   * Triggers when an action or filter link is clicked.
   */
  @event('action', true)
  private _actionEvent: EventEmitter<{ name: KatFilterBarAction }>;

  private _handleSearchClick() {
    this._actionEvent.emit({ name: KatFilterBarAction.SEARCH });
  }

  private _handleCreateClick(e: MouseEvent) {
    e.preventDefault();
    this._actionEvent.emit({ name: KatFilterBarAction.CREATE });
  }

  private _handleEditClick(e: MouseEvent) {
    e.preventDefault();
    this._actionEvent.emit({ name: KatFilterBarAction.EDIT });
  }

  private handleSlotChange = () => {
    this.requestUpdate();
  };

  // Only actions rendered in the "actions" slot
  private _hasActions() {
    return (
      this.showActionApply ||
      this.showActionClear ||
      this.showActionFilters ||
      this.showActionSettings ||
      this.showActionSort
    );
  }

  private _hasQuickFilters() {
    const slots = checkSlots(this);
    return (
      !!slots['quick-filters'] || this.showFilterCreate || this.showFilterEdit
    );
  }

  private _hasSearch() {
    const slots = checkSlots(this);

    return !!slots.search || this.showActionSearch;
  }

  private _getRenderedQuickFilters() {
    if (!this._hasQuickFilters()) {
      return nothing;
    }

    const createText = getString(
      'kat-filter-bar-create',
      undefined,
      this.locale
    );

    const editText = getString('kat-filter-bar-edit', undefined, this.locale);

    const createRendered = this.showFilterCreate
      ? html`<kat-link href="#" @click=${this._handleCreateClick}
          >${createText}</kat-link
        >`
      : nothing;
    const editRendered = this.showFilterEdit
      ? html`<kat-link href="#" @click=${this._handleEditClick}
          >${editText}</kat-link
        >`
      : nothing;

    return html`<div class="quick-filters">
      <slot name="quick-filters" @slotchange=${this.handleSlotChange}></slot>
      ${createRendered} ${editRendered}
    </div>`;
  }

  private _getRenderedSearch() {
    if (!this._hasSearch()) {
      return nothing;
    }

    const text = getString('kat-filter-bar-search', undefined, this.locale);

    const button = this.showActionSearch
      ? html`<kat-button
          class="toggle-text-or-icon"
          id="search"
          variant="secondary"
          @click=${this._handleSearchClick}
          ><span>${text}</span><kat-icon name="search" size="small"></kat-icon
        ></kat-button>`
      : nothing;

    return html`<div class="search">
      <slot name="search" @slotchange=${this.handleSlotChange}></slot>
      ${button}
    </div>`;
  }

  private _getRenderedFilters() {
    const slots = checkSlots(this);

    if (!slots.default) {
      return nothing;
    }

    return html`<div class="filters">
      <slot @slotchange=${this.handleSlotChange}></slot>
    </div>`;
  }

  private _getRenderedActions() {
    const actions = [
      {
        id: 'clear',
        stringId: 'kat-filter-bar-clear',
        iconName: 'clear',
        visible: this.showActionClear,
        handler: () =>
          this._actionEvent.emit({ name: KatFilterBarAction.CLEAR }),
      },
      {
        id: 'apply',
        stringId: 'kat-filter-bar-apply',
        iconName: 'check',
        variant: 'secondary',
        visible: this.showActionApply,
        handler: () =>
          this._actionEvent.emit({ name: KatFilterBarAction.APPLY }),
      },
      {
        id: 'sort',
        iconName: 'sort',
        visible: this.showActionSort,
        handler: () =>
          this._actionEvent.emit({ name: KatFilterBarAction.SORT }),
      },
      {
        id: 'filters',
        iconName: 'all_filters',
        visible: this.showActionFilters,
        handler: () =>
          this._actionEvent.emit({ name: KatFilterBarAction.FILTERS }),
      },
      {
        id: 'settings',
        iconName: 'settings',
        visible: this.showActionSettings,
        handler: () =>
          this._actionEvent.emit({ name: KatFilterBarAction.SETTINGS }),
      },
    ].map(({ id, stringId, iconName, handler, variant, visible }) => {
      if (!visible) {
        return nothing;
      }

      const hasText = !!stringId;

      const text = hasText
        ? html`<span
            >${getString(stringId as any, undefined, this.locale)}</span
          >`
        : nothing;

      const cssClasses = classMap({
        'toggle-text-or-icon': hasText,
      });

      const icon = html`<kat-icon name="${iconName}" size="small"></kat-icon>`;

      return html`<kat-button
        class=${cssClasses}
        id=${id}
        @click=${handler}
        variant=${variant ?? 'utility-link'}
        >${text}${icon}</kat-button
      >`;
    });

    return this._hasActions()
      ? html`<div class="actions">
          <div class="actions-content">${actions}</div>
        </div>`
      : nothing;
  }

  render() {
    const hasActions = this._hasActions();
    const hasQuickFilters = this._hasQuickFilters();
    const hasSearch = this._hasSearch();

    const cssClasses = classMap({
      wrapper: true,
      'has-actions': hasActions,
      'no-actions': !hasActions,
      'has-quick-filters': hasQuickFilters,
      'no-quick-filters': !hasQuickFilters,
      'has-search': hasSearch,
      'no-search': !hasSearch,
    });

    return html`<div class=${cssClasses} @slotchange=${this.handleSlotChange}>
      ${this._getRenderedSearch()}${this._getRenderedQuickFilters()}
      ${this._getRenderedFilters()}${this._getRenderedActions()}
    </div>`;
  }
}
